import { api } from '../core';
import { makeSelectBundlePageIdByPageIndex, RootState, setViewportPageIdForRestorePageIndexInStore } from '@/store';
import { refreshBundleSectionsWithCurrentFilters } from './getBundleSections';

// API returns empty object on success
type ReorderBundleProviderResponse = Record<string, never>;

interface ReorderBundleProviderProps {
  bundleId: string;
  targetIndex: number;
  providerSections: string[];
  abortController?: AbortController;
}

const reorderBundleProvider = api.injectEndpoints({
  endpoints: (builder) => ({
    reorderBundleProvider: builder.mutation<ReorderBundleProviderResponse, ReorderBundleProviderProps>({
      query: ({
        bundleId,
        targetIndex,
        providerSections,
        abortController = new AbortController(),
      }: ReorderBundleProviderProps) => ({
        url: 'bundle/provider/reorder',
        method: 'POST',
        signal: abortController.signal,
        body: {
          bundleId,
          targetIndex,
          providerSections,
        },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as RootState;

          // Get current page ID to maintain viewport position
          const getBundlePageIdByPageIndex = makeSelectBundlePageIdByPageIndex(state);
          const currentPageId = getBundlePageIdByPageIndex(state.bundleViewportData.viewportPageIndexInStore);

          if (state.sharedData.activeBundleType) {
            // Maintain current viewport position
            dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

            refreshBundleSectionsWithCurrentFilters(dispatch, state);
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in reorderBundleProvider in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useReorderBundleProviderMutation } = reorderBundleProvider;
